// Shared resource-drawer persist/delete flows (ADR 0012).
//
// product / resource-type / membership-plan drawers all route their footer
// Save/Delete through the same submit()->toast->emit and
// confirm->delete->toast->emit sequences. These two helpers hold that sequence
// once so each drawer only wires its store calls + i18n keys.
//
// Services are typed structurally (like submission.ts) so utils keeps no
// design-system / toast runtime dependency.

import {
  submit,
  type FieldTree,
  type ReadonlyFieldTree,
} from '@angular/forms/signals';
import { firstValueFrom, type Observable } from 'rxjs';
import {
  focusFirstInvalid,
  routeServerError,
  type ServerErrorRule,
} from './submission';

interface Toaster {
  success(message: string): unknown;
  error(message: string): unknown;
}

interface Confirmer {
  confirm(config: {
    title: string;
    text: string;
    confirmButtonText: string;
    rejectButtonText?: string;
  }): Observable<unknown>;
}

export interface DrawerSaveMessages {
  readonly createSuccess: string;
  readonly createError: string;
  readonly updateSuccess: string;
  readonly updateError: string;
}

export interface DrawerSaveConfig {
  readonly toast: Toaster;
  readonly isCreate: boolean;
  readonly create: () => Observable<unknown>;
  readonly update: () => Observable<unknown>;
  readonly messages: DrawerSaveMessages;
  readonly onSaved: () => void;
  /**
   * Optional server-field → form-field routing (063 mapper). When a save is
   * rejected, a field-scoped server error (`error.field`) routes onto its form
   * field and the drawer stays open; `errorRules` cover non-field cases a field
   * name can't express (e.g. a bare duplicate-name/SKU 409 conflict). Anything
   * that doesn't route falls back to the generic error toast.
   */
  readonly fieldMap?: Record<string, ReadonlyFieldTree<unknown>>;
  readonly errorRules?: readonly ServerErrorRule[];
}

/**
 * Footer Save persist for a drawer (button lives outside `<form>`, ADR 0012
 * category-3): route through `submit()` for mark-touched + validate +
 * `focusFirstInvalid`, then `await` the create/update so its result feeds
 * `submit()`'s server-error channel. On success it toasts + emits; on rejection
 * a field-scoped error routes onto its field (drawer stays open), otherwise the
 * generic error toast surfaces as before. Returns the `submit()` promise.
 */
export function runDrawerSave<TModel>(
  form: FieldTree<TModel>,
  config: DrawerSaveConfig,
): Promise<boolean> {
  return submit(form, {
    action: async () => {
      const source$ = config.isCreate ? config.create() : config.update();
      const success = config.isCreate
        ? config.messages.createSuccess
        : config.messages.updateSuccess;
      const failure = config.isCreate
        ? config.messages.createError
        : config.messages.updateError;
      try {
        await firstValueFrom(source$);
        config.toast.success(success);
        config.onSaved();
        return undefined;
      } catch (err) {
        const routed = routeServerError(
          err,
          config.fieldMap ?? {},
          config.errorRules ?? [],
        );
        if (routed) return routed;
        config.toast.error(failure);
        return undefined;
      }
    },
    onInvalid: focusFirstInvalid,
  });
}

export interface DrawerDeleteMessages {
  readonly title: string;
  readonly text: string;
  readonly confirm: string;
  readonly cancel: string;
  readonly success: string;
  readonly error: string;
}

export interface DrawerDeleteConfig {
  readonly toast: Toaster;
  readonly delete$: () => Observable<unknown>;
  readonly messages: DrawerDeleteMessages;
  readonly onDeleted: () => void;
}

/**
 * Footer Delete for a drawer: confirm, then delete on confirmation, toasting +
 * emitting on the result.
 */
export function confirmAndDelete(
  dialog: Confirmer,
  config: DrawerDeleteConfig,
): void {
  dialog
    .confirm({
      title: config.messages.title,
      text: config.messages.text,
      confirmButtonText: config.messages.confirm,
      rejectButtonText: config.messages.cancel,
    })
    .subscribe((confirmed) => {
      if (confirmed) {
        config.delete$().subscribe({
          next: () => {
            config.toast.success(config.messages.success);
            config.onDeleted();
          },
          error: () => {
            config.toast.error(config.messages.error);
          },
        });
      }
    });
}
